import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { User, Lock, Mail, X, LogIn, UserPlus, ArrowLeft } from "lucide-react";
import { requestPasswordReset } from "@/services/authService";
import PasswordStrengthBar from "@/components/ui/PasswordStrengthBar";

const AuthModal = ({
  isOpen,
  onClose,
  onLogin,
  onRegister,
  routeData,
  loading,
  error,
  serverFieldErrors = {},
  initialTab = "login",
}) => {
  // Login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Registration state
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Validation state (client-side errors merged with server field errors)
  const [regErrors, setRegErrors] = useState({});
  const mergedErrors = { ...regErrors, ...serverFieldErrors };

  // Password strength state from server
  const [passwordCheck, setPasswordCheck] = useState(null); // { valid, strength, message, details }
  const debounceRef = useRef(null);

  // Debounced password strength check
  useEffect(() => {
    if (!regPassword || regPassword.length < 4) {
      setPasswordCheck(null);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.post("/lmiga/user/check-password.json", {
          password: regPassword,
        });
        setPasswordCheck(res.data?.data || null);
      } catch {
        setPasswordCheck(null);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [regPassword]);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState(null);

  // Active tab state
  const [activeTab, setActiveTab] = useState(initialTab);

  // Handle forgot password submission
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError(null);
    try {
      await requestPasswordReset(forgotEmail);
      setForgotSuccess(true);
    } catch (err) {
      setForgotError(err.message || "Failed to send reset email");
    } finally {
      setForgotLoading(false);
    }
  };

  const backToLogin = () => {
    setShowForgotPassword(false);
    setForgotSuccess(false);
    setForgotError(null);
    setForgotEmail("");
  };

  // Handle form submission for login
  const handleLogin = (e) => {
    e.preventDefault();
    onLogin({ email, password, routeData });
  };

  // Handle form submission for registration
  const handleRegister = (e) => {
    e.preventDefault();

    // Validate registration
    const errors = {};
    if (!regEmail || !regEmail.includes("@")) {
      errors.email = "Please enter a valid email address";
    }

    if (!regPassword || regPassword.length < 8) {
      errors.password = "Password must be at least 8 characters long";
    }

    if (regPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(errors).length > 0) {
      setRegErrors(errors);
      return;
    }

    // Clear any previous errors
    setRegErrors({});

    // Submit registration data.  Email format and vacation dates are account
    // settings edited on the My Account page; registration defaults to HTML
    // format and no vacation dates (gateway#11).
    onRegister({
      email: regEmail,
      password: regPassword,
      routeData,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="relative">
            <Button
              onClick={onClose}
              variant="ghost"
              className="absolute right-2 top-2 p-1 h-auto w-auto text-gray-500 dark:text-gray-300"
            >
              <X className="h-4 w-4" />
            </Button>
            <CardTitle>Register / Login</CardTitle>
            <CardDescription>
              Login or create an account to save your route and receive email
              alerts
            </CardDescription>
          </CardHeader>

          <Tabs
            defaultValue="login"
            value={activeTab}
            onValueChange={setActiveTab}
          >
            <TabsList className="grid grid-cols-2 mx-6">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="register">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              {showForgotPassword ? (
                <div>
                  <CardContent className="space-y-4 pt-4">
                    {forgotSuccess ? (
                      <div className="text-center space-y-3">
                        <div className="text-green-600 dark:text-green-400 font-medium">
                          Check your email for a password reset link.
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          If you don't see it, check your spam folder.
                        </p>
                      </div>
                    ) : (
                      <form
                        onSubmit={handleForgotPassword}
                        className="space-y-4"
                      >
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Enter your email address and we'll send you a link to
                          reset your password.
                        </p>
                        <div className="space-y-2">
                          <label htmlFor="forgot-email">Email:</label>
                          <div className="relative">
                            <div className="absolute left-3 top-3 text-gray-400 dark:text-gray-500">
                              <Mail className="h-4 w-4" />
                            </div>
                            <Input
                              id="forgot-email"
                              type="email"
                              placeholder="Enter your email"
                              className="pl-10"
                              value={forgotEmail}
                              onChange={(e) => setForgotEmail(e.target.value)}
                              required
                            />
                          </div>
                        </div>
                        {forgotError && (
                          <Alert variant="destructive">
                            <AlertDescription>{forgotError}</AlertDescription>
                          </Alert>
                        )}
                        <Button
                          type="submit"
                          className="w-full"
                          disabled={forgotLoading}
                        >
                          {forgotLoading ? "Sending..." : "Send Reset Link"}
                        </Button>
                      </form>
                    )}
                  </CardContent>
                  <CardFooter>
                    <button
                      type="button"
                      className="flex items-center text-sm text-blue-500 dark:text-blue-400 hover:underline"
                      onClick={backToLogin}
                    >
                      <ArrowLeft className="h-3 w-3 mr-1" />
                      Back to Login
                    </button>
                  </CardFooter>
                </div>
              ) : (
                <form onSubmit={handleLogin}>
                  <CardContent className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <label htmlFor="email">Email:</label>
                      <div className="relative">
                        <div className="absolute left-3 top-3 text-gray-400 dark:text-gray-500">
                          <Mail className="h-4 w-4" />
                        </div>
                        <Input
                          id="email"
                          type="email"
                          placeholder="Enter your email"
                          className="pl-10"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="password">Password:</label>
                      <div className="relative">
                        <div className="absolute left-3 top-3 text-gray-400 dark:text-gray-500">
                          <Lock className="h-4 w-4" />
                        </div>
                        <Input
                          id="password"
                          type="password"
                          placeholder="Enter your password"
                          className="pl-10"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  </CardContent>

                  <CardFooter className="flex-col gap-2">
                    <Button type="submit" className="w-full" disabled={loading}>
                      <LogIn className="mr-2 h-4 w-4" />
                      {loading ? "Logging in..." : "Login"}
                    </Button>
                    <button
                      type="button"
                      className="text-sm text-blue-500 dark:text-blue-400 hover:underline mt-1"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot password?
                    </button>
                    <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Don't have an account?{" "}
                      <button
                        type="button"
                        className="text-blue-500 dark:text-blue-400 hover:underline"
                        onClick={() => setActiveTab("register")}
                      >
                        Register
                      </button>
                    </p>
                  </CardFooter>
                </form>
              )}
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister}>
                <CardContent className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <label htmlFor="reg-email">Email:</label>
                    <div className="relative">
                      <div className="absolute left-3 top-3 text-gray-400 dark:text-gray-500">
                        <Mail className="h-4 w-4" />
                      </div>
                      <Input
                        id="reg-email"
                        type="email"
                        placeholder="Enter your email"
                        className={`pl-10 ${
                          mergedErrors.email ? "border-red-500" : ""
                        }`}
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        required
                      />
                    </div>
                    {mergedErrors.email && (
                      <p className="text-red-500 dark:text-red-400 text-xs">
                        {mergedErrors.email}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="reg-password">Password:</label>
                    <div className="relative">
                      <div className="absolute left-3 top-3 text-gray-400 dark:text-gray-500">
                        <Lock className="h-4 w-4" />
                      </div>
                      <Input
                        id="reg-password"
                        type="password"
                        placeholder="Create a password"
                        className={`pl-10 ${
                          mergedErrors.password ? "border-red-500" : ""
                        }`}
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        required
                      />
                    </div>
                    {passwordCheck && (
                      <PasswordStrengthBar
                        strength={passwordCheck.strength}
                        valid={passwordCheck.valid}
                        details={passwordCheck.details}
                      />
                    )}
                    {mergedErrors.password && (
                      <p className="text-red-500 dark:text-red-400 text-xs">
                        {mergedErrors.password}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="confirm-password">Confirm Password:</label>
                    <div className="relative">
                      <div className="absolute left-3 top-3 text-gray-400 dark:text-gray-500">
                        <Lock className="h-4 w-4" />
                      </div>
                      <Input
                        id="confirm-password"
                        type="password"
                        placeholder="Confirm your password"
                        className={`pl-10 ${
                          mergedErrors.confirmPassword ? "border-red-500" : ""
                        }`}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                    </div>
                    {mergedErrors.confirmPassword && (
                      <p className="text-red-500 dark:text-red-400 text-xs">
                        {mergedErrors.confirmPassword}
                      </p>
                    )}
                  </div>
                </CardContent>

                <CardFooter className="flex-col gap-2">
                  <Button type="submit" className="w-full" disabled={loading}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    {loading ? "Creating Account..." : "Create Account"}
                  </Button>
                  <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Already have an account?{" "}
                    <button
                      type="button"
                      className="text-blue-500 dark:text-blue-400 hover:underline"
                      onClick={() => setActiveTab("login")}
                    >
                      Login
                    </button>
                  </p>
                </CardFooter>
              </form>
            </TabsContent>
          </Tabs>

          {error && (
            <div className="px-6 pb-6">
              <Alert variant="destructive">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AuthModal;
